import { Component, OnInit, inject, signal, computed, HostListener, ViewChild, ElementRef, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { PickerModule } from '@ctrl/ngx-emoji-mart';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  PreventionCampaignsService,
  CampaignType,
  CampaignScope,
  CampaignStatus,
  CampaignDetail,
  EligibleSite,
  CampaignSite,
  DeliverabilityStatus,
  CampaignOfferRefusalCode,
} from '../../core/services/prevention-campaigns.service';
import { PreventionOffersService, OrganizationOfferSummary } from '../../core/services/prevention-offers.service';
import { ToastService } from '../../core/services/toast.service';
import { DateTimePickerComponent } from '../../component/date-time-picker/date-time-picker.component';
import { SiteMultiSelectComponent, SiteMultiSelectOption } from '../../component/site-multi-select/site-multi-select.component';
import { ConfirmModalComponent } from '../../component/confirm-modal/confirm-modal.component';

/**
 * Écran unique création/modification "Comm prévention". Sites de diffusion modifiables après
 * création (Lot 2026-09-09, remplace l'ancienne portée immuable) : SCHEDULED/ACTIVE restent
 * éditables (confirmation affichée pour ACTIVE si la sélection change réellement), ENDED passe en
 * lecture seule (backend refuse aussi tout changement de sites sur une campagne terminée, jamais
 * seulement ce garde-fou Angular).
 */
@Component({
  selector: 'app-comm-prevention-form',
  standalone: true,
  imports: [CommonModule, TranslatePipe, DateTimePickerComponent, SiteMultiSelectComponent, ConfirmModalComponent, PickerModule],
  templateUrl: './comm-prevention-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./comm-prevention-form.component.scss'],
})
export class CommPreventionFormComponent implements OnInit {
  private api = inject(PreventionCampaignsService);
  private offersApi = inject(PreventionOffersService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private toastService = inject(ToastService);
  private translate = inject(TranslateService);

  mode = signal<'create' | 'edit'>('create');
  campaignId: string | null = null;
  loadingCampaign = signal(false);

  subject = signal('');
  titre = signal('');
  texte = signal('');
  imageData = signal<string | null>(null);

  /** Doit rester synchronisé avec maxlength="1000" sur le <textarea> #cpTexte du template. */
  readonly TEXTE_MAX_LENGTH = 1000;

  /** Bibliothèque de smileys sur le champ "Texte" -- même composant (emoji-mart) que le composeur d'actualités. */
  showEmojiPicker = signal(false);
  @ViewChild('cpTexteArea') texteArea?: ElementRef<HTMLTextAreaElement>;

  toggleEmojiPicker(): void {
    this.showEmojiPicker.update(v => !v);
  }

  addEmoji(event: any): void {
    const emoji = event?.emoji?.native;
    if (!emoji) return;

    const el = this.texteArea?.nativeElement;
    const current = this.texte();
    const start = el?.selectionStart ?? current.length;
    const end = el?.selectionEnd ?? current.length;

    // maxlength du textarea bloque la saisie clavier mais pas l'insertion programmatique -- refuse
    // l'ajout plutôt que de tronquer au milieu (un slice() aveugle sur la limite pourrait couper
    // l'emoji en deux, paire de substitution UTF-16, laissant un caractère invalide).
    if (current.length - (end - start) + emoji.length > this.TEXTE_MAX_LENGTH) return;

    this.texte.set(current.slice(0, start) + emoji + current.slice(end));
    this.showEmojiPicker.set(false);

    if (el) {
      const caret = start + emoji.length;
      queueMicrotask(() => { el.focus(); el.setSelectionRange(caret, caret); });
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target || typeof target.closest !== 'function') return;
    if (!target.closest('.emoji-picker') && !target.closest('.btn-emoji')) {
      this.showEmojiPicker.set(false);
    }
  }

  type = signal<CampaignType>('TEST_INTEREST');
  offers = signal<OrganizationOfferSummary[]>([]);
  offerId = signal<string | null>(null);

  scope = signal<CampaignScope>('ALL');
  eligibleSites = signal<EligibleSite[]>([]);
  selectedSiteIds = signal<Set<string>>(new Set());
  /** Sites au chargement de l'écran (édition) -- sert à calculer si la sélection a réellement changé (confirmation ACTIVE) et à ne jamais retirer silencieusement un site déjà ciblé devenu inéligible entre-temps. */
  initialSiteIds = signal<Set<string>>(new Set());
  campaignSites = signal<CampaignSite[]>([]);
  campaignStatus = signal<CampaignStatus | null>(null);
  showSitesConfirm = signal(false);

  /**
   * État opérationnel (C6) -- jamais recalculé ici, uniquement reçu tel quel de {@link CampaignDetail}
   * (voir `applyCampaignDetail`). Absent en mode `create` (pas encore de campagne existante à
   * évaluer) : reste `null`, aucun bandeau affiché, cohérent avec `NOT_APPLICABLE`.
   */
  deliverabilityStatus = signal<DeliverabilityStatus | null>(null);
  /** Motif global (`BLOCKED` uniquement) -- code brut `CampaignOfferRefusal`, jamais affiché tel
   *  quel (voir {@link deliverabilityReasonLabel}). */
  deliverabilityReason = signal<CampaignOfferRefusalCode | null>(null);
  /** Identifiants des sites bloqués (`PARTIALLY_BLOCKED` uniquement) -- leur nom est retrouvé via
   *  {@link blockedSiteNames}, jamais une seconde requête. */
  blockedEstablishmentIds = signal<string[]>([]);

  /** Résout les noms des sites bloqués depuis {@link campaignSites} (déjà chargé avec le détail) --
   *  jamais une nouvelle source de vérité, jamais un recalcul d'éligibilité côté frontend. */
  blockedSiteNames = computed<string[]>(() => {
    const blocked = new Set(this.blockedEstablishmentIds());
    if (blocked.size === 0) return [];
    return this.campaignSites()
      .filter(s => blocked.has(s.establishmentId))
      .map(s => s.name);
  });

  /**
   * Traduit {@link deliverabilityReason} (code technique `CampaignOfferRefusal`) en message lisible
   * -- jamais le code brut affiché au laboratoire (interdiction explicite). Repli générique si le
   * code est absent ou non reconnu (ex. nouvelle valeur d'enum ajoutée côté backend avant que ce
   * frontend ne soit mis à jour) -- jamais une clé i18n non résolue affichée telle quelle.
   */
  deliverabilityReasonLabel = computed<string>(() => {
    const reason = this.deliverabilityReason();
    const key = reason ? `commPrevention.form.deliverability.reason.${reason}` : '';
    const translated = reason ? this.translate.instant(key) : '';
    return translated && translated !== key
      ? translated
      : this.translate.instant('commPrevention.form.deliverability.reason.UNKNOWN');
  });

  startsAtLocal = signal<string>('');
  endsAtLocal = signal<string>('');

  saving = signal(false);
  errorMessage = signal<string | null>(null);

  eligibleCount = computed(() => this.eligibleSites().filter(s => s.eligible).length);
  totalSitesCount = computed(() => this.eligibleSites().length);

  /**
   * Valeur à binder sur le <select> natif "Offre associée" -- ne se contente pas de lire
   * `offerId()` seul : `loadOffers()` (peuple les <option>) et `loadCampaign()` (pose `offerId`)
   * sont deux appels HTTP concurrents à `ngOnInit`, dans un ordre non garanti. Si `offerId` est
   * posé AVANT que l'<option> correspondante existe dans le DOM, un simple binding `[value]`
   * natif ne se corrige jamais tout seul une fois l'<option> apparue (contrairement à
   * `[ngModel]`/`[formControl]`) car il ne se ré-évalue que si `offerId()` lui-même change --
   * jamais si seul `offers()` change. Ce computed lit aussi `offers()` pour forcer sa
   * ré-évaluation (et donc la ré-application de `.value` au DOM) dès que la liste arrive.
   */
  selectedOfferId = computed(() => (this.offers().length > 0 ? this.offerId() : null));

  /**
   * Contrôle des dates de publication (aucune côté serveur avant ce lot, voir
   * PreventionCampaignService#validateSchedule côté backend) : évite les campagnes dont la
   * fenêtre est incohérente (fin avant/à l'heure du début) ou déjà "morte" à la création (fin
   * déjà passée). En édition, une campagne déjà terminée reste éditable sans forcer une nouvelle
   * date de fin dans le futur -- seule l'incohérence début > fin est bloquante dans ce cas.
   */
  dateError = computed<string | null>(() => {
    const start = this.parseLocalDate(this.startsAtLocal());
    const end = this.parseLocalDate(this.endsAtLocal());
    if (start && end && end.getTime() <= start.getTime()) {
      return this.translate.instant('commPrevention.form.errors.endBeforeStart');
    }
    if (this.mode() === 'create' && end && end.getTime() <= Date.now()) {
      return this.translate.instant('commPrevention.form.errors.endInPast');
    }
    return null;
  });

  /** Options du sélecteur multi-sites -- un site déjà ciblé (initialSiteIds) reste toujours sélectionnable même s'il a perdu son éligibilité entre-temps (comportement le moins destructif, voir PreventionCampaignService#updateCampaign côté backend) ; seul un NOUVEL ajout est bloqué s'il n'est pas éligible. */
  siteOptions = computed<SiteMultiSelectOption[]>(() => {
    const initial = this.initialSiteIds();
    return this.eligibleSites().map(s => ({
      id: s.establishmentId,
      name: s.name,
      disabled: !s.eligible && !initial.has(s.establishmentId),
      disabledReason: !s.eligible ? this.translate.instant('commPrevention.form.siteNotEligible') : undefined,
    }));
  });

  /** En création, seule scope=SITES exige une sélection explicite (ALL se résout côté serveur) ; en édition, une sélection est toujours requise (plus de notion de scope, voir UpdateCampaignInput). */
  private sitesRequired = computed(() => this.mode() === 'edit' || this.scope() === 'SITES');

  sitesError = computed<string | null>(() => {
    if (!this.sitesRequired()) return null;
    if (this.selectedSiteIds().size === 0) return this.translate.instant('commPrevention.form.errors.noSiteSelected');
    return null;
  });

  /** Sélection réellement différente de l'état chargé -- déclenche la confirmation pour une campagne ACTIVE. */
  sitesChanged = computed(() => {
    const initial = this.initialSiteIds();
    const current = this.selectedSiteIds();
    if (initial.size !== current.size) return true;
    for (const id of initial) {
      if (!current.has(id)) return true;
    }
    return false;
  });

  isEnded = computed(() => this.mode() === 'edit' && this.campaignStatus() === 'ENDED');

  isValid = computed(() => {
    if (!this.subject().trim() || !this.titre().trim() || !this.texte().trim()) return false;
    if (this.type() === 'PROMOTE_OFFER' && !this.offerId()) return false;
    if (this.sitesError()) return false;
    if (this.dateError()) return false;
    return true;
  });

  ngOnInit(): void {
    this.loadOffers();
    this.loadEligibleSites();

    const id = this.route.snapshot.paramMap.get('campaignId');
    if (id) {
      this.mode.set('edit');
      this.campaignId = id;
      this.loadCampaign(id);
    }
  }

  private loadOffers(): void {
    this.offersApi.getOffers().subscribe({
      next: (offers) => this.offers.set(offers.filter(o => o.active)),
      error: () => this.offers.set([])
    });
  }

  /**
   * `subject` (C6) : transmis tel quel au backend, source unique de vérité sur l'éligibilité par
   * sujet (topic clinique, etc.) -- ce composant ne recalcule jamais rien lui-même, il ne fait que
   * relire `subject()` et le retransmettre.
   */
  private loadEligibleSites(): void {
    const offerId = this.type() === 'PROMOTE_OFFER' ? this.offerId() : null;
    const subject = this.subject().trim() || null;
    this.api.listEligibleSites(offerId, subject).subscribe({
      next: (sites) => {
        this.eligibleSites.set(sites);
        // Retire une sélection devenue inéligible SUITE À UN CHANGEMENT D'OFFRE fait dans cette
        // même session -- mais jamais un site qui faisait déjà partie de la campagne au chargement
        // (initialSiteIds) : celui-là reste sélectionné tel quel même si son éligibilité a dérivé
        // depuis, comportement le moins destructif (voir audit Phase 1, même règle côté backend).
        const eligibleIds = new Set(sites.filter(s => s.eligible).map(s => s.establishmentId));
        const initial = this.initialSiteIds();
        this.selectedSiteIds.update(current =>
          new Set([...current].filter(id => eligibleIds.has(id) || initial.has(id))));
      },
      error: () => this.eligibleSites.set([])
    });
  }

  /**
   * Applique un CampaignDetail au formulaire -- factorisé pour être réutilisé après un
   * enregistrement réussi en édition (voir performSave) : `router.navigate` vers l'URL COURANTE
   * est un no-op pour Angular Router (même piège que le bug "Annuler" du 2026-09-08), donc
   * `loadCampaign` n'est JAMAIS rappelé après un save -- sans cette resynchronisation explicite,
   * `initialSiteIds`/`campaignStatus` resteraient figés sur l'état du chargement de page initial,
   * et la confirmation "campagne ACTIVE + sites changés" comparerait à tort la sélection au tout
   * premier état de la session plutôt qu'au dernier état réellement persisté.
   */
  private applyCampaignDetail(detail: CampaignDetail): void {
    this.subject.set(detail.subject);
    this.titre.set(detail.titre);
    this.texte.set(detail.texte);
    this.imageData.set(detail.imageData);
    this.type.set(detail.type);
    this.offerId.set(detail.offerId);
    this.campaignSites.set(detail.sites);
    this.campaignStatus.set(detail.status);
    // C6 -- absent/`undefined` sur une réponse backend antérieure au lot : `?? null`/`?? []`
    // retombe sur l'équivalent NOT_APPLICABLE (aucun bandeau affiché), jamais une erreur.
    this.deliverabilityStatus.set(detail.deliverabilityStatus ?? null);
    this.deliverabilityReason.set(detail.deliverabilityReason ?? null);
    this.blockedEstablishmentIds.set(detail.blockedEstablishmentIds ?? []);
    const siteIds = new Set(detail.sites.map(s => s.establishmentId));
    this.initialSiteIds.set(siteIds);
    this.selectedSiteIds.set(new Set(siteIds));
    this.startsAtLocal.set(this.toLocalInput(detail.startsAt));
    this.endsAtLocal.set(this.toLocalInput(detail.endsAt));
  }

  private loadCampaign(id: string): void {
    this.loadingCampaign.set(true);
    this.api.getCampaign(id).subscribe({
      next: (detail) => {
        this.applyCampaignDetail(detail);
        this.loadingCampaign.set(false);
        this.loadEligibleSites();
      },
      error: () => {
        this.loadingCampaign.set(false);
        this.errorMessage.set(this.translate.instant('commPrevention.form.errors.loadFailed'));
      }
    });
  }

  /**
   * Recharge l'éligibilité des sites quand le sujet est modifié (C6) -- déclenché au `blur` du
   * champ Sujet plutôt qu'à chaque frappe (`input`) pour ne jamais spammer le backend pendant la
   * saisie, même principe que le reste de ce formulaire (aucun débounce existant à réutiliser,
   * volontairement pas introduit pour un seul champ -- voir `PREVENTION_CAMPAIGN_OPERATIONAL_
   * INTEGRITY_C6_UI_LOT3_DESIGN.md` § API_CONTRACT_TO_CONFIRM).
   */
  onSubjectBlurred(): void {
    this.loadEligibleSites();
  }

  setType(type: CampaignType): void {
    this.type.set(type);
    if (type === 'TEST_INTEREST') {
      this.offerId.set(null);
    }
    this.loadEligibleSites();
  }

  onOfferChanged(offerId: string): void {
    this.offerId.set(offerId || null);
    this.loadEligibleSites();
  }

  setScope(scope: CampaignScope): void {
    this.scope.set(scope);
  }

  onImageSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    try {
      this.validateImage(file);
    } catch (err: any) {
      this.toastService.show(this.translate.instant('commPrevention.form.errors.errorTitle'), 'error', 4000, err.message);
      input.value = '';
      return;
    }
    this.fileToBase64(file).then(b64 => this.imageData.set(b64));
  }

  removeImage(): void {
    this.imageData.set(null);
  }

  onStartsAtChanged(value: string): void {
    this.startsAtLocal.set(value);
  }

  onEndsAtChanged(value: string): void {
    this.endsAtLocal.set(value);
  }

  /**
   * Bug trouvé en QA (2026-09-08, "le btn annuler ne fonctionne pas") : `router.navigate` vers
   * l'URL COURANTE (`/comm-prevention/:id`, déjà affichée) est un no-op par défaut pour Angular
   * Router (`onSameUrlNavigation: 'ignore'`) -- le clic ne faisait donc RIEN de visible en mode
   * édition, les champs modifiés restaient affichés tels quels. Corrigé en rechargeant les données
   * serveur de la campagne (jamais une navigation) : "Annuler" reste sur la fiche et réinitialise
   * les champs à leur valeur enregistrée, cohérent avec "Enregistrer" qui reste aussi sur la fiche.
   */
  cancel(): void {
    if (this.mode() === 'edit' && this.campaignId) {
      this.loadCampaign(this.campaignId);
    } else {
      this.router.navigate(['/comm-prevention']);
    }
  }

  /**
   * Lien de retour discret en tête de page (ajustement UX 2026-09-08) -- distinct de {@link #cancel},
   * qui réinitialise la fiche en mode édition plutôt que de quitter la page.
   * `?returnTo=` (posé par le chevron de la page "Campagne de prévention", voir
   * prevention-demand.component.ts#openCampaignForTopic) ramène vers l'écran d'origine plutôt que
   * la liste par défaut -- absent dans tous les autres cas d'entrée sur ce formulaire.
   */
  goToList(): void {
    const returnTo = this.route.snapshot.queryParamMap.get('returnTo');
    this.router.navigate([returnTo || '/comm-prevention']);
  }

  /**
   * Une campagne ACTIVE dont la sélection de sites a réellement changé demande confirmation
   * avant enregistrement (pas d'alerte pour SCHEDULED, ni si seul le contenu a changé) -- voir
   * {@link showSitesConfirm}/{@link confirmSitesChange}. Backend refuse de toute façon tout
   * changement de sites sur une campagne ENDED, ce garde-fou Angular n'est qu'un confort en plus.
   */
  submit(): void {
    if (!this.isValid() || this.saving()) return;
    if (this.mode() === 'edit' && this.campaignStatus() === 'ACTIVE' && this.sitesChanged()) {
      this.showSitesConfirm.set(true);
      return;
    }
    this.performSave();
  }

  confirmSitesChange(): void {
    this.showSitesConfirm.set(false);
    this.performSave();
  }

  cancelSitesChange(): void {
    this.showSitesConfirm.set(false);
  }

  private performSave(): void {
    this.saving.set(true);
    this.errorMessage.set(null);

    const startsAt = this.fromLocalInput(this.startsAtLocal());
    const endsAt = this.fromLocalInput(this.endsAtLocal());
    const offerId = this.type() === 'PROMOTE_OFFER' ? this.offerId() : null;

    if (this.mode() === 'create') {
      this.api.createCampaign({
        subject: this.subject().trim(),
        titre: this.titre().trim(),
        texte: this.texte().trim(),
        imageData: this.imageData(),
        offerId,
        scope: this.scope(),
        establishmentIds: this.scope() === 'SITES' ? [...this.selectedSiteIds()] : undefined,
        startsAt,
        endsAt,
      }).subscribe({
        next: () => {
          this.saving.set(false);
          this.toastService.show(
            this.translate.instant('commPrevention.toast.createdTitle'), 'success', 3000,
            this.translate.instant('commPrevention.toast.createdMessage'));
          // Retour liste après création (ajustement UX 2026-09-08) -- Comm prévention reste avant
          // tout un écran de gestion de campagnes, jamais la fiche fraîchement créée.
          this.router.navigate(['/comm-prevention']);
        },
        error: (err) => this.handleError(err)
      });
    } else if (this.campaignId) {
      this.api.updateCampaign(this.campaignId, {
        subject: this.subject().trim(),
        titre: this.titre().trim(),
        texte: this.texte().trim(),
        imageData: this.imageData(),
        offerId,
        establishmentIds: [...this.selectedSiteIds()],
        startsAt,
        endsAt,
      }).subscribe({
        next: (updated) => {
          this.saving.set(false);
          // Resynchronise le formulaire sur l'état RÉELLEMENT persisté (voir applyCampaignDetail) :
          // la navigation ci-dessous vers l'URL courante est un no-op pour Angular Router, jamais
          // un rechargement -- sans ça, initialSiteIds resterait figé sur l'état d'avant ce save.
          this.applyCampaignDetail(updated);
          this.toastService.show(
            this.translate.instant('commPrevention.toast.savedTitle'), 'success', 3000,
            this.translate.instant('commPrevention.toast.savedMessage'));
          this.router.navigate(['/comm-prevention', updated.campaignId]);
        },
        error: (err) => this.handleError(err)
      });
    }
  }

  private handleError(err: any): void {
    this.saving.set(false);
    const message = err?.error?.message || this.translate.instant('commPrevention.form.errors.generic');
    this.errorMessage.set(message);
  }

  private validateImage(file: File): void {
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    const maxSize = 2 * 1024 * 1024;
    if (!validTypes.includes(file.type)) {
      throw new Error(this.translate.instant('commPrevention.form.errors.invalidImageFormat'));
    }
    if (file.size > maxSize) {
      throw new Error(this.translate.instant('commPrevention.form.errors.imageTooLarge'));
    }
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /** ISO OffsetDateTime -> valeur locale pour <input type="datetime-local"> (pas de secondes/fuseau). */
  private toLocalInput(iso: string | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  /** Valeur locale <input type="datetime-local"> -> ISO OffsetDateTime (null si vide). */
  private fromLocalInput(local: string): string | null {
    const d = this.parseLocalDate(local);
    return d ? d.toISOString() : null;
  }

  private parseLocalDate(local: string): Date | null {
    if (!local) return null;
    const d = new Date(local);
    return isNaN(d.getTime()) ? null : d;
  }
}
